import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Phone, Sparkles, X, Send } from "lucide-react";
import { useApp } from "../contexts/AppContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "smm_chat_session";

function getSessionId() {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
        id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(SESSION_KEY, id);
    }
    return id;
}

const FloatingActions = () => {
    const { lang } = useApp();
    const [chatOpen, setChatOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const scrollRef = useRef(null);
    const sessionId = useRef(getSessionId());

    // Load history on first open
    useEffect(() => {
        if (!chatOpen || messages.length > 0) return;
        fetch(`${API_BASE}/api/chat/history/${sessionId.current}`)
            .then((r) => r.json())
            .then((data) => {
                if (Array.isArray(data) && data.length) {
                    setMessages(data.map((m) => ({ role: m.role, content: m.content, id: m.id })));
                } else {
                    setMessages([{ role: "assistant", content: lang === "te"
                        ? "నమస్కారం! శ్రీ మాత మల్టీసర్వీసెస్‌కు స్వాగతం. మీకు ఏ విధంగా సహాయపడగలను?"
                        : "Hi there! I'm Sri Matha's virtual assistant. Ask me about any service, form, timings or how to book & track your application.",
                        id: "welcome" }]);
                }
            })
            .catch(() => {
                setMessages([{ role: "assistant", content: "Hi! How can I help you today?", id: "welcome" }]);
            });
    }, [chatOpen, messages.length, lang]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming]);

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setStreaming(true);
        const userMsg = { role: "user", content: text, id: `u-${Date.now()}` };
        const assistantMsg = { role: "assistant", content: "", id: `a-${Date.now()}` };
        setMessages((m) => [...m, userMsg, assistantMsg]);

        try {
            const resp = await fetch(`${API_BASE}/api/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message: text }),
            });
            if (!resp.ok || !resp.body) throw new Error("stream failed");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split("\n");
                buf = lines.pop() || "";
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    try {
                        const evt = JSON.parse(line.slice(6));
                        if (evt.delta) {
                            setMessages((m) => m.map((msg, i) =>
                                i === m.length - 1 ? { ...msg, content: msg.content + evt.delta } : msg
                            ));
                        } else if (evt.error) {
                            setMessages((m) => m.map((msg, i) =>
                                i === m.length - 1 ? { ...msg, content: `⚠️ ${evt.error}` } : msg
                            ));
                        }
                    } catch (_) { /* ignore malformed chunk */ }
                }
            }
        } catch (e) {
            setMessages((m) => m.map((msg, i) =>
                i === m.length - 1 ? { ...msg, content: "⚠️ Sorry, I couldn't reach the assistant. Please try again or WhatsApp us at +91 9000767404." } : msg
            ));
        } finally {
            setStreaming(false);
        }
    };

    return (
        <>
            <div className="fixed bottom-5 right-5 z-40 flex flex-col gap-3">
                <button data-testid="chat-toggle" onClick={() => setChatOpen(!chatOpen)}
                    className="group relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-200"
                    style={{ background: "linear-gradient(135deg, hsl(var(--royal)) 0%, hsl(var(--royal-deep)) 100%)" }}
                    aria-label="AI chat">
                    {chatOpen ? <X size={22} /> : <Sparkles size={22} />}
                    {!chatOpen && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[hsl(var(--gold))] ring-2 ring-white animate-pulse" />
                    )}
                </button>
                <a data-testid="whatsapp-float" href="https://wa.me/919000767404?text=Hello%20Sri%20Matha%20Multiservices" target="_blank" rel="noreferrer"
                    className="w-14 h-14 rounded-full bg-[#25D366] shadow-xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-200"
                    aria-label="Chat on WhatsApp">
                    <MessageCircle size={26} fill="white" strokeWidth={0} />
                </a>
                <a data-testid="call-float" href="tel:+919000767404"
                    className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-200"
                    style={{ background: "linear-gradient(180deg, hsl(var(--royal)) 0%, hsl(var(--royal-deep)) 100%)" }}
                    aria-label="Call">
                    <Phone size={22} />
                </a>
            </div>

            {chatOpen && (
                <div data-testid="chat-panel"
                    className="fixed z-40 bottom-24 right-5 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[75vh] sm:h-[540px] max-h-[calc(100vh-8rem)] rounded-3xl bg-card border border-border shadow-2xl flex flex-col overflow-hidden"
                    style={{ backdropFilter: "blur(20px)" }}>
                    <header className="flex items-center gap-3 px-4 py-3 border-b border-border"
                        style={{ background: "linear-gradient(135deg, hsl(var(--royal)) 0%, hsl(var(--royal-deep)) 100%)" }}>
                        <div className="w-9 h-9 rounded-full bg-white p-1 shadow">
                            <img src="/logo.webp" alt="Sri Matha" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0 text-white">
                            <div className="font-semibold text-sm leading-tight">Sri Matha Assistant</div>
                            <div className="text-[11px] text-white/80 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Online · Powered by Gemini
                            </div>
                        </div>
                        <button onClick={() => setChatOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/10 text-white flex items-center justify-center" aria-label="Close chat">
                            <X size={16} />
                        </button>
                    </header>

                    <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/20">
                        {messages.map((m) => (
                            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-[hsl(var(--royal))] text-white rounded-br-md"
                                        : "bg-background border border-border text-foreground rounded-bl-md"
                                }`}>
                                    {m.content || <span className="inline-flex gap-1 opacity-60">
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0.15s" }} />
                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0.3s" }} />
                                    </span>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <footer className="p-3 border-t border-border bg-card">
                        <div className="flex gap-2 items-end">
                            <textarea data-testid="chat-input" rows={1} value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                placeholder={lang === "te" ? "మీ ప్రశ్న టైప్ చేయండి…" : "Ask about any service…"}
                                className="flex-1 resize-none rounded-2xl border border-border bg-background px-4 py-2.5 text-sm focus:border-[hsl(var(--royal))] focus:outline-none max-h-24" />
                            <button data-testid="chat-send" onClick={send} disabled={!input.trim() || streaming}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow disabled:opacity-40 shrink-0"
                                style={{ background: "linear-gradient(135deg, hsl(var(--royal)) 0%, hsl(var(--royal-deep)) 100%)" }}
                                aria-label="Send">
                                <Send size={16} />
                            </button>
                        </div>
                        <p className="mt-1.5 text-[10px] text-muted-foreground text-center">
                            Powered by Gemini · Do not share sensitive personal information.
                        </p>
                    </footer>
                </div>
            )}
        </>
    );
};

export default FloatingActions;
